import { ChevronLeft } from "lucide-react";
import React, { useState } from "react";
import { useNavigate } from "react-router";
import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Typography from "@mui/material/Typography";
import { Button } from "@components/ui/button";
import SimpleBar from "simplebar-react";
import { Textarea } from "@components/ui/textarea";
import { useForm, SubmitHandler } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import "simplebar-react/dist/simplebar.min.css";
import {
  addIcpProfile,
  IcpProfile,
  updateIcpProfile,
} from "@redux/slices/strategicPlanning";
import useAuth from "@hooks/useAuth";
import LoadingScreen from "@components/LoadingScreen";
import { Input } from "@components/ui/input";
import { addNotification } from "@redux/slices/notifications";

const steps = ["", "", "", "", "", ""];

const AddIcp: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = React.useState(0);
  const [skipped, setSkipped] = React.useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [profileId, setProfileId] = useState("");
  const { user } = useAuth();

  const [formData, setFormData] = useState<IcpProfile>({
    user_id: user?.id,
    user_name: user?.first_name + " " + user?.last_name,
    name: "",
    status: "",
    question1: "",
    question2: "",
    question3: "",
    question4: "",
    question5: "",
    question6: "",
    question7: "",
    question8: "",
    question9: "",
    question10: "",
    question11: "",
    question12: "",
    question13: "",
    question14: "",
    question15: "",
    question16: "",
    question17: "",
    question18: "",
    question19: "",
    question20: "",
    question21: "",
    question22: "",
    question23: "",
    question24: "",
    question25: "",
  });

  const icpSchema = z.object({
    // user_id: z.number().default(user?.id),
    // user_name: z.string().default(user?.first_name +" "+ user?.last_name),
    // status:z.string().default("completed"),
    name: z.string().min(1, "This field is required"),
    question1: z.string().min(1, "This field is required"),
    question2: z.string().min(1, "This field is required"),
    question3: z.string().min(1, "This field is required"),
    question4: z.string().min(1, "This field is required"),
    question5: z.string().min(1, "This field is required"),
    question6: z.string().min(1, "This field is required"),

    question7: z.string().min(1, "This field is required"),
    question8: z.string().min(1, "This field is required"),
    question9: z.string().min(1, "This field is required"),
    question10: z.string().min(1, "This field is required"),
    question11: z.string().min(1, "This field is required"),

    question12: z.string().min(1, "This field is required"),
    question13: z.string().min(1, "This field is required"),
    question14: z.string().min(1, "This field is required"),
    question15: z.string().min(1, "This field is required"),
    question16: z.string().min(1, "This field is required"),

    question17: z.string().min(1, "This field is required"),
    question18: z.string().min(1, "This field is required"),
    question19: z.string().min(1, "This field is required"),
    question20: z.string().min(1, "This field is required"),

    question21: z.string().min(1, "This field is required"),
    question22: z.string().min(1, "This field is required"),
    question23: z.string().min(1, "This field is required"),

    question24: z.string().min(1, "This field is required"),
    question25: z.string().min(1, "This field is required"),
  });

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  type ICPFormData = z.infer<typeof icpSchema>;

  // Initialize React Hook Form
  const {
    register,
    handleSubmit,
    formState: { errors },
    trigger,
    getValues,
  } = useForm<ICPFormData>({
    resolver: zodResolver(icpSchema),
    mode: "onBlur",
  });

  type ICPFieldNames = keyof ICPFormData;

  const isStepOptional = (step: number) => {
    return false;
  };

  const isStepSkipped = (step: number) => {
    return skipped.has(step);
  };

  const handleNext = async () => {
    const currentStepFields = getStepFields(activeStep);
    const isValid = await trigger(currentStepFields);
    if (!isValid) return;

    let newSkipped = skipped;
    if (isStepSkipped(activeStep)) {
      newSkipped = new Set(newSkipped.values());
      newSkipped.delete(activeStep);
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped(newSkipped);
    addProfile();
  };

  const handleReturn = async () => {
    addProfile();
    navigate(-1)
  };
  
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSkip = async () => {
    if (!isStepOptional(activeStep)) {
      throw new Error("You can't skip a step that isn't optional.");
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped((prevSkipped) => {
      const newSkipped = new Set(prevSkipped.values());
      newSkipped.add(activeStep);
      return newSkipped;
    });
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  const getStepFields = (step: number): ICPFieldNames[] => {
    switch (step) {
      case 0:
        return [
          "question1",
          "question2",
          "question3",
          "question4",
          "question5",
          "question6",
        ];
      case 1:
        return [
          "question7",
          "question8",
          "question9",
          "question10",
          "question11",
        ];
      case 2:
        return [
          "question12",
          "question13",
          "question14",
          "question15",
          "question16",
        ];
      case 3:
        return ["question17", "question18", "question19", "question20"];
      case 4:
        return ["question21", "question22", "question23"];
      case 5:
        return ["question24", "question25"];
      default:
        return [];
    }
  };

  const onSubmit: SubmitHandler<ICPFormData> = (data) => {
    addProfile();
  };

  const addProfile = async () => {
    try {
      let updatedFormData = { ...formData };

      if (formData?.question24 != "" && formData.question25 != "") {
        console.log("first");
        updatedFormData = {
          ...updatedFormData,
          status: "completed",
        };
        // const handleAddNotification = async () => {
          await addNotification("Icp Added", `${user?.email} added icp `);
          // const data = await getAllNotifications();
          // if (data) {
          //   setNotifications(data);
          // }
        // };
      } else {
        console.log("first");
        updatedFormData = {
          ...updatedFormData,
          status: "draft",
        };
      }

      setLoading(true);

      if (profileId == "") {
        const data = await addIcpProfile(
          updatedFormData,
          "Profile added/updated Successfully"
        );
        setProfileId(data.id);
        console.log("Saaaaaaaa", data);
      } else {
        updateIcpProfile(
          profileId,
          updatedFormData,
          "Profile updated successfully"
        );
      }

      if (updatedFormData?.status === "completed") {
        navigate(`/strategicPlanning/icps/${user?.id}`);
      }

      setLoading(false);
    } catch (error) {
      console.error("Error in addProfile:", error);
      setLoading(false);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <div className="px-5">
            <h4 className="text-xl font-medium mb-10 text-center">
              Customer Identification and Demographics
            </h4>
            {/* Question 1 */}
            <div className="mb-4">
              <p className="text-[#71717A] mb-2">
                1. Who are your best current customers and why are they the
                best?
              </p>
              <Textarea
                {...register("question1")}
                className="h-24 w-full"
                placeholder="Your answer..."
                value={formData?.question1}
                onChange={handleInputChange}
                // value={}
              />
              {errors.question1 && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.question1.message}
                </p>
              )}
            </div>
            {/* Question 2 */}
            <div className="mb-4">
              <p className="text-[#71717A] mb-2">
                2. In which industry are your best customers active?
              </p>
              <Textarea
                {...register("question2")}
                className="h-24 w-full"
                placeholder="Your answer..."
                value={formData?.question2}
                onChange={handleInputChange}
              />
              {errors.question2 && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.question2.message}
                </p>
              )}
            </div>
            {/* Question 3 */}
            <div className="mb-4">
              <p className="text-[#71717A] mb-2">
                3. How large are the companies (number of employees, annual
                revenue) of your best customers?
              </p>
              <Textarea
                {...register("question3")}
                className="h-24 w-full"
                placeholder="Your answer..."
                value={formData?.question3}
                onChange={handleInputChange}
              />
              {errors.question3 && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.question3.message}
                </p>
              )}
            </div>
            {/* Question 4 */}
            <div className="mb-4">
              <p className="text-[#71717A] mb-2">
                4. What positions or roles do the decision-makers in these
                companies hold?
              </p>
              <Textarea
                {...register("question4")}
                className="h-24 w-full"
                placeholder="Your answer..."
                value={formData?.question4}
                onChange={handleInputChange}
              />
              {errors.question4 && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.question4.message}
                </p>
              )}
            </div>
            {/* Question 5 */}
            <div className="mb-4">
              <p className="text-[#71717A] mb-2">
                5. What demographic characteristics (age, gender, education) do
                your best customers have?
              </p>
              <Textarea
                {...register("question5")}
                className="h-24 w-full"
                placeholder="Your answer..."
                value={formData?.question5}
                onChange={handleInputChange}
              />
              {errors.question5 && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.question5.message}
                </p>
              )}
            </div>
            {/* Question 6 */}
            <div className="mb-4">
              <p className="text-[#71717A] mb-2">
                6. Where are these companies geographically located?
              </p>
              <Textarea
                {...register("question6")}
                className="h-24 w-full"
                placeholder="Your answer..."
                value={formData?.question6}
                onChange={handleInputChange}
              />
              {errors.question6 && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.question6.message}
                </p>
              )}
            </div>
          </div>
        );
      case 1:
        return (
          <div className="px-5">
            <h4 className="text-xl font-medium mb-10 text-center">
              Needs and Challenges
            </h4>
            {/* Question 7 */}
            <div className="mb-4">
              <p className="text-[#71717A] mb-2">
                7. What specific problems or challenges do your best customers
                have that your product/service can solve?
              </p>
              <Textarea
                {...register("question7")}
                className="h-24 w-full"
                placeholder="Your answer..."
                value={formData?.question7}
                onChange={handleInputChange}
              />
              {errors.question7 && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.question7.message}
                </p>
              )}
            </div>
            {/* Question 8 */}
            <div className="mb-4">
              <p className="text-[#71717A] mb-2">
                8. What goals do your best customers pursue, and how does your
                product/service help them achieve these goals?
              </p>
              <Textarea
                {...register("question8")}
                className="h-24 w-full"
                placeholder="Your answer..."
                value={formData?.question8}
                onChange={handleInputChange}
              />
              {errors.question8 && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.question8.message}
                </p>
              )}
            </div>
            {/* Question 9 */}
            <div className="mb-4">
              <p className="text-[#71717A] mb-2">
                9. What products or services did your best customers use before
                adopting your solution?
              </p>
              <Textarea
                {...register("question9")}
                className="h-24 w-full"
                placeholder="Your answer..."
                value={formData?.question9}
                onChange={handleInputChange}
              />
              {errors.question9 && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.question9.message}
                </p>
              )}
            </div>
            {/* Question 10 */}
            <div className="mb-4">
              <p className="text-[#71717A] mb-2">
                10. What factors most influence the purchasing decisions of your
                best customers (price, quality, support, etc.)?
              </p>
              <Textarea
                {...register("question10")}
                className="h-24 w-full"
                placeholder="Your answer..."
                value={formData?.question10}
                onChange={handleInputChange}
              />
              {errors.question10 && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.question10.message}
                </p>
              )}
            </div>
            {/* Question 11 */}
            <div className="mb-4">
              <p className="text-[#71717A] mb-2">
                11. What values and beliefs are important to your best
                customers?
              </p>
              <Textarea
                {...register("question11")}
                className="h-24 w-full"
                placeholder="Your answer..."
                value={formData?.question11}
                onChange={handleInputChange}
              />
              {errors.question11 && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.question11.message}
                </p>
              )}
            </div>
          </div>
        );
      case 2:
        return (
          <div className="px-5">
            <h4 className="text-xl font-medium mb-10 text-center">
              Purchase Process and Decision Making
            </h4>
            {/* Question 12 */}
            <div className="mb-4">
              <p className="text-[#71717A] mb-2">
                12. What does the typical purchase process of your best
                customers look like (from need recognition to purchase
                decision)?
              </p>
              <Textarea
                {...register("question12")}
                className="h-24 w-full"
                placeholder="Your answer..."
                value={formData?.question12}
                onChange={handleInputChange}
              />
              {errors.question12 && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.question12.message}
                </p>
              )}
            </div>
            {/* Question 13 */}
            <div className="mb-4">
              <p className="text-[#71717A] mb-2">
                13. What objections or concerns do your best customers have
                before purchasing your solution?
              </p>
              <Textarea
                {...register("question13")}
                className="h-24 w-full"
                placeholder="Your answer..."
                value={formData?.question13}
                onChange={handleInputChange}
              />
              {errors.question13 && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.question13.message}
                </p>
              )}
            </div>
            {/* Question 14 */}
            <div className="mb-4">
              <p className="text-[#71717A] mb-2">
                14. What features or functions of your product/service do your
                best customers appreciate the most?
              </p>
              <Textarea
                {...register("question14")}
                className="h-24 w-full"
                placeholder="Your answer..."
                value={formData?.question14}
                onChange={handleInputChange}
              />
              {errors.question14 && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.question14.message}
                </p>
              )}
            </div>
            {/* Question 15 */}
            <div className="mb-4">
              <p className="text-[#71717A] mb-2">
                15. What is the average customer lifetime value in your company?
              </p>
              <Textarea
                {...register("question15")}
                className="h-24 w-full"
                placeholder="Your answer..."
                value={formData?.question15}
                onChange={handleInputChange}
              />
              {errors.question15 && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.question15.message}
                </p>
              )}
            </div>
            {/* Question 16 */}
            <div className="mb-4">
              <p className="text-[#71717A] mb-2">
                16. What type of customer feedback do you receive from your best
                customers and how do they use your product/service?
              </p>
              <Textarea
                {...register("question16")}
                className="h-24 w-full"
                placeholder="Your answer..."
                value={formData?.question16}
                onChange={handleInputChange}
              />
              {errors.question16 && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.question16.message}
                </p>
              )}
            </div>
          </div>
        );
      case 3:
        return (
          <div className="px-5">
            <h4 className="text-xl font-medium mb-10 text-center">
              Marketing and Sales Channels
            </h4>
            {/* Question 17 */}
            <div className="mb-4">
              <p className="text-[#71717A] mb-2">
                17. What marketing channels do you primarily use to reach your
                customers (e.g., email, social media, SEO, PPC)?
              </p>
              <Textarea
                {...register("question17")}
                className="h-24 w-full"
                placeholder="Your answer..."
                value={formData?.question17}
                onChange={handleInputChange}
              />
              {errors.question17 && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.question17.message}
                </p>
              )}
            </div>
            {/* Question 18 */}
            <div className="mb-4">
              <p className="text-[#71717A] mb-2">
                18. What sales channels are most important for your business
                (e.g., direct sales, partner sales, online sales)?
              </p>
              <Textarea
                {...register("question18")}
                className="h-24 w-full"
                placeholder="Your answer..."
                value={formData?.question18}
                onChange={handleInputChange}
              />
              {errors.question18 && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.question18.message}
                </p>
              )}
            </div>
            {/* Question 19 */}
            <div className="mb-4">
              <p className="text-[#71717A] mb-2">
                19. What messages or value propositions do you communicate to
                your customers?
              </p>
              <Textarea
                {...register("question19")}
                className="h-24 w-full"
                placeholder="Your answer..."
                value={formData?.question19}
                onChange={handleInputChange}
              />
              {errors.question19 && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.question19.message}
                </p>
              )}
            </div>
            {/* Question 20 */}
            <div className="mb-4">
              <p className="text-[#71717A] mb-2">
                20. What tools and technologies do you use for your sales and
                marketing management?
              </p>
              <Textarea
                {...register("question20")}
                className="h-24 w-full"
                placeholder="Your answer..."
                value={formData?.question20}
                onChange={handleInputChange}
              />
              {errors.question20 && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.question20.message}
                </p>
              )}
            </div>
          </div>
        );
      case 4:
        return (
          <div className="px-5">
            <h4 className="text-xl font-medium mb-10 text-center">
              Competition and Market Position
            </h4>
            {/* Question 21 */}
            <div className="mb-4">
              <p className="text-[#71717A] mb-2">
                21. Who are your main competitors?
              </p>
              <Textarea
                {...register("question21")}
                className="h-24 w-full"
                placeholder="Your answer..."
                value={formData?.question21}
                onChange={handleInputChange}
              />
              {errors.question21 && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.question21.message}
                </p>
              )}
            </div>
            {/* Question 22 */}
            <div className="mb-4">
              <p className="text-[#71717A] mb-2">
                22. What are your key unique selling points compared to your
                competitors?
              </p>
              <Textarea
                {...register("question22")}
                className="h-24 w-full"
                placeholder="Your answer..."
                value={formData?.question22}
                onChange={handleInputChange}
              />
              {errors.question22 && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.question22.message}
                </p>
              )}
            </div>
            {/* Question 23 */}
            <div className="mb-4">
              <p className="text-[#71717A] mb-2">
                23. How do you position yourself in the market compared to your
                competitors?
              </p>
              <Textarea
                {...register("question23")}
                className="h-24 w-full"
                placeholder="Your answer..."
                value={formData?.question23}
                onChange={handleInputChange}
              />
              {errors.question23 && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.question23.message}
                </p>
              )}
            </div>
          </div>
        );
      case 5:
        return (
          <div className="px-5">
            <h4 className="text-xl font-medium mb-10 text-center">
              Customer Relationship and Satisfaction
            </h4>
            {/* Question 24 */}
            <div className="mb-4">
              <p className="text-[#71717A] mb-2">
                24. How do you measure customer satisfaction?
              </p>
              <Textarea
                {...register("question24")}
                className="h-24 w-full"
                placeholder="Your answer..."
                value={formData?.question24}
                onChange={handleInputChange}
              />
              {errors.question24 && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.question24.message}
                </p>
              )}
            </div>
            {/* Question 25 */}
            <div className="mb-4">
              <p className="text-[#71717A] mb-2">
                25. What measures do you take to strengthen customer loyalty?
              </p>
              <Textarea
                {...register("question25")}
                className="h-24 w-full"
                placeholder="Your answer..."
                value={formData?.question25}
                onChange={handleInputChange}
              />
              {errors.question25 && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.question25.message}
                </p>
              )}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div>
      {loading ? (
        <LoadingScreen />
      ) : (
        <div>
          {/* Header */}
          <div className="flex justify-between items-center mb-5">
            <h4 className="text-xl font-bold flex items-center -ms-2">
              <ChevronLeft
                size={22}
                className="me-3 cursor-pointer"
                onClick={() => navigate(-1)}
              />
              Add Ideal Customer Profile
            </h4>
            <div className="flex items-start">
              <p className="mt-2">Profile Name: </p>
              <div>
                <Input
                  {...register("name")}
                  className="w-full ms-2"
                  placeholder="Profile Name"
                  value={formData?.name}
                  onChange={handleInputChange}
                  // value={}
                />
                {errors.name && (
                  <p className="text-red-500 text-sm ms-2 mt-1">
                    {errors.name.message}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Form Container */}
          <div className="px-56 mt-10">
            <Box sx={{ width: "100%" }}>
              {/* Stepper */}
              <Stepper activeStep={activeStep}>
                {steps.map((label, index) => {
                  const stepProps: { completed?: boolean } = {};
                  const labelProps: { optional?: React.ReactNode } = {};
                  if (isStepSkipped(index)) {
                    stepProps.completed = false;
                  }
                  return (
                    <Step key={label} {...stepProps}>
                      <StepLabel {...labelProps}>{label}</StepLabel>
                    </Step>
                  );
                })}
              </Stepper>

              {/* Form Content */}
              {activeStep === steps.length ? (
                // All steps completed
                <React.Fragment>
                  <Typography sx={{ mt: 2, mb: 1 }}>
                    All steps completed - you&apos;re finished
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
                    <Box sx={{ flex: "1 1 auto" }} />
                    <Button onClick={handleReset}>Reset</Button>
                  </Box>
                </React.Fragment>
              ) : (
                // Current Step Form
                <form onSubmit={handleSubmit(onSubmit)}>
                  <React.Fragment>
                    <div>
                      <SimpleBar style={{ maxHeight: 400 }} className="mt-8">
                        {renderStepContent(activeStep)}
                      </SimpleBar>
                    </div>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        pt: 2,
                      }}
                    >
                      <Button className="bg-[#1CB5AF]" disabled={activeStep === 0} onClick={handleBack}>
                        Back
                      </Button>
                      <Box sx={{ flex: "1 1 auto" }} />
                      {isStepOptional(activeStep) && (
                        <Button className="bg-[#1CB5AF]"  onClick={handleSkip}>
                          Skip
                        </Button>
                      )}
                      {activeStep === steps.length - 1 ? (
                        <Button type="submit">Save</Button>
                      ) : (
                        <div className="flex items-center">
                         <Button  className="bg-[#1CB5AF] me-2" type="button" onClick={handleReturn}>
                          Save & Return
                        </Button>
                        <Button className="bg-[#1CB5AF]" type="button" onClick={handleNext}>
                          Save & Next
                        </Button>
                        </div>
                      )}
                    </Box>
                  </React.Fragment>
                </form>
              )}
            </Box>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddIcp;
